// "Continuar con Google / Apple / Facebook" con las pautas de marca de cada uno (decisiones D48 y D51). Google: la "G"
// de cuatro colores sin cambios, el texto oficial, fondo blanco con borde #747775 (oscuro: #131314 con borde #8E918F) y
// Roboto Medium. Apple y Facebook, abajo. El alto se agranda al de los otros botones de la tienda, como permiten las pautas.
import { cx, Icono } from '@reparto/ui';
import { type ButtonHTMLAttributes, useState } from 'react';

/** La foto de la cuenta de Google; si no hay o no carga, la "G". */
export function FotoGoogle({ url }: { url: string | null | undefined }) {
  const [fallo, setFallo] = useState(false);
  if (url && !fallo)
    return (
      <img
        src={url}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setFallo(true)}
        className="h-10 w-10 shrink-0 rounded-full"
      />
    );
  return (
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-stone-100">
      <LogoGoogle tamano={22} />
    </span>
  );
}

export function LogoGoogle({ tamano = 20 }: { tamano?: number }) {
  return (
    <svg aria-hidden="true" width={tamano} height={tamano} viewBox="0 0 48 48" className="shrink-0">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

type PropsBoton = ButtonHTMLAttributes<HTMLButtonElement> & { cargando?: boolean; texto?: string };

export function BotonGoogle({ cargando, texto = 'Continuar con Google', className, disabled, ...resto }: PropsBoton) {
  return (
    <button
      type="button"
      {...resto}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      className={cx(
        'flex h-14 w-full items-center justify-center gap-3 rounded-full border px-5',
        'font-[Roboto,Arial,sans-serif] text-[17px] font-medium tracking-[0.25px]',
        'border-[#747775] bg-[#ffffff] text-[#1f1f1f] hover:bg-[#f8faff] active:bg-[#eef2fb]',
        'oscuro:border-[#8e918f] oscuro:bg-[#131314] oscuro:text-[#e3e3e3] oscuro:hover:bg-[#1f2023]',
        'focus-visible:ring-4 focus-visible:ring-primary-200 focus-visible:outline-none',
        'disabled:cursor-wait disabled:opacity-70',
        className,
      )}
    >
      {cargando ? (
        <Icono nombre="progress_activity" tamano={20} className="animate-spin text-[#4285f4]" />
      ) : (
        <LogoGoogle />
      )}
      <span>{texto}</span>
    </button>
  );
}

/** El logo de Apple (sin cambios de forma ni de proporción, como piden sus pautas). */
export function LogoApple({ tamano = 20 }: { tamano?: number }) {
  return (
    <svg aria-hidden="true" width={tamano} height={tamano} viewBox="0 0 24 24" className="shrink-0" fill="currentColor">
      <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z" />
    </svg>
  );
}

/** El logo de Facebook: la "f" calada en el círculo, sobre el azul del botón. */
export function LogoFacebook({ tamano = 22 }: { tamano?: number }) {
  return (
    <svg aria-hidden="true" width={tamano} height={tamano} viewBox="0 0 24 24" className="shrink-0">
      <path
        fill="#ffffff"
        d="M24 12.07C24 5.41 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07"
      />
    </svg>
  );
}

/**
 * "Continuar con Apple" con las pautas de Apple para la web: negro con el logo y el texto en blanco (en modo oscuro, al
 * revés: blanco con negro), la letra del sistema y el logo a la izquierda del texto, del mismo alto que los otros.
 */
export function BotonApple({ cargando, texto = 'Continuar con Apple', className, disabled, ...resto }: PropsBoton) {
  return (
    <button
      type="button"
      {...resto}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      className={cx(
        'flex h-14 w-full items-center justify-center gap-2.5 rounded-full px-5',
        'font-[system-ui,-apple-system,BlinkMacSystemFont,sans-serif] text-[17px] font-medium',
        'bg-[#000000] text-[#ffffff] hover:bg-[#1d1d1f] active:bg-[#2c2c2e]',
        'oscuro:bg-[#ffffff] oscuro:text-[#000000] oscuro:hover:bg-[#f2f2f7]',
        'focus-visible:ring-4 focus-visible:ring-primary-200 focus-visible:outline-none',
        'disabled:cursor-wait disabled:opacity-70',
        className,
      )}
    >
      {cargando ? <Icono nombre="progress_activity" tamano={20} className="animate-spin" /> : <LogoApple />}
      <span>{texto}</span>
    </button>
  );
}

/**
 * "Continuar con Facebook" con las pautas de Meta: el azul de Facebook (#0866FF, el de la marca desde 2023, con el que
 * el texto blanco pasa 4,5:1), el logo blanco a la izquierda y el texto en blanco. Igual en los dos modos.
 */
export function BotonFacebook({
  cargando,
  texto = 'Continuar con Facebook',
  className,
  disabled,
  ...resto
}: PropsBoton) {
  return (
    <button
      type="button"
      {...resto}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      className={cx(
        'flex h-14 w-full items-center justify-center gap-2.5 rounded-full px-5',
        'font-[Helvetica,Arial,system-ui,sans-serif] text-[17px] font-bold',
        'bg-[#0866ff] text-[#ffffff] hover:bg-[#0759e0] active:bg-[#064fc7]',
        'focus-visible:ring-4 focus-visible:ring-primary-200 focus-visible:outline-none',
        'disabled:cursor-wait disabled:opacity-70',
        className,
      )}
    >
      {cargando ? <Icono nombre="progress_activity" tamano={20} className="animate-spin" /> : <LogoFacebook />}
      <span>{texto}</span>
    </button>
  );
}
